/**
 * Handles length/distance conversion calculations
 * Uses feet as the intermediate unit for all conversions
 */
export class LengthConverter {
  /**
   * Conversion factors to feet
   * @private
   */
  static TO_FEET = {
    inches: 1 / 12,
    feet: 1,
    yards: 3,
    miles: 5280,
    mm: 0.00328084,
    cm: 0.0328084,
    meters: 3.28084,
    km: 3280.84
  }

  /**
   * Conversion factors from feet
   * @private
   */
  static FROM_FEET = {
    inches: 12,
    feet: 1,
    yards: 1 / 3,
    miles: 1 / 5280,
    mm: 304.8,
    cm: 30.48,
    meters: 1 / 3.28084,
    km: 1 / 3280.84
  }

  /**
   * Round a number to 4 decimal places
   * @private
   */
  round(value) {
    return Math.round((value + 0.00001) * 10000) / 10000
  }

  /**
   * Convert a value from one unit to another
   * @param {number} value - The value to convert
   * @param {string} fromUnit - The source unit
   * @param {string} toUnit - The target unit
   * @returns {number} The converted value
   */
  convert(value, fromUnit, toUnit) {
    if (fromUnit === toUnit) {
      return value
    }

    const toFeetFactor = LengthConverter.TO_FEET[fromUnit]
    const fromFeetFactor = LengthConverter.FROM_FEET[toUnit]

    if (toFeetFactor === undefined || fromFeetFactor === undefined) {
      throw new Error(`Unknown unit: ${fromUnit} or ${toUnit}`)
    }

    const valueInFeet = value * toFeetFactor
    return valueInFeet * fromFeetFactor
  }

  /**
   * Convert a value to all supported units
   * @param {number} value - The value to convert
   * @param {string} fromUnit - The source unit
   * @returns {Object} Object containing all converted values
   */
  convertToAll(value, fromUnit) {
    const results = {}
    const units = this.getSupportedUnits()

    for (const toUnit of units) {
      results[toUnit] = this.round(this.convert(value, fromUnit, toUnit))
    }

    return results
  }

  /**
   * Get list of supported units
   * @returns {string[]} Array of supported unit names
   */
  getSupportedUnits() {
    return Object.keys(LengthConverter.TO_FEET)
  }
}
